import { useState, useRef } from 'react';
import { Dialog, CONFIG, api, useToast } from './_barrel.js';


        // Upload a broker's returned data file for a specific logged DSAR request.
        // It is stored as evidence, ingested through the consent gate, and the
        // device is re-analysed — the tab then shows the updated grade.
        function AeaIngestResponseDialog({ deviceId, brokerRequest, token, onClose, onDone }) {
            const toast = useToast();
            const fileRef = useRef(null);
            const [file, setFile] = useState(null);
            const [busy, setBusy] = useState(false);
            const [stage, setStage] = useState(null);
            const [err, setErr] = useState(null);
            const [result, setResult] = useState(null);

            async function submit() {
                if (!file) { setErr('Choose the broker response file first.'); return; }
                setBusy(true); setErr(null); setResult(null);
                try {
                    setStage('Ingesting file…');
                    const fd = new FormData();
                    fd.append('broker_request_id', brokerRequest.id);
                    fd.append('file', file);
                    const resp = await fetch(`${CONFIG.apiBase}/api/v1/aea/devices/${deviceId}/ingest`,
                        { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
                    if (!resp.ok) {
                        let msg = `Ingest failed (${resp.status})`;
                        try { const j = await resp.json(); msg = j.detail || j.error?.message || msg; } catch (_) {}
                        throw new Error(msg);
                    }
                    const ing = await resp.json();
                    setStage('Re-analysing exposure…');
                    await api(`/api/v1/aea/devices/${deviceId}/analyse`, { method: 'POST' }, token);
                    setResult(ing);
                    toast(`Ingested ${ing.inserted} points`);
                } catch (ex) { setErr(ex.message); setStage(null); }
                finally { setBusy(false); }
            }

            return (
                <Dialog onClose={onClose} title={`Upload response — ${brokerRequest.broker_name}`}>
                    <div className="space-y-4">
                        {!result && (
                            <>
                                <div className="text-sm text-muted leading-relaxed">
                                    Upload the location data the broker returned for this
                                    <span className="font-mono"> {(brokerRequest.request_type || '').replace('_', ' ')} </span>
                                    request. Accepted: CSV or JSON of location points (as most brokers return), or a
                                    Google-style <span className="font-mono">Records.json</span>. It is stored as
                                    evidence and ingested through the consent gate.
                                </div>
                                <input ref={fileRef} type="file" accept=".json,.csv,.xlsx,.gpx,.zip"
                                       onChange={e => setFile(e.target.files && e.target.files[0])} style={{ display: 'none' }} />
                                <div className="flex items-center gap-3">
                                    <button type="button" className="btn-ghost" onClick={() => fileRef.current && fileRef.current.click()}>
                                        {file ? 'Change file' : 'Choose file'}
                                    </button>
                                    <span className="text-xs text-muted">{file ? file.name : 'No file chosen'}</span>
                                </div>
                                {busy && stage && <div className="text-xs text-muted">{stage}</div>}
                                {err && <div className="text-sm" style={{ color: 'var(--red)' }}>{err}</div>}
                                <div className="flex justify-end gap-3 pt-2">
                                    <button type="button" className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
                                    <button type="button" className="btn-gold" disabled={busy || !file} onClick={submit}>
                                        {busy ? 'Working…' : 'Upload & analyse'}
                                    </button>
                                </div>
                            </>
                        )}
                        {result && (
                            <>
                                <div className="text-sm" style={{ color: 'var(--green)' }}>✓ Response ingested and exposure re-analysed.</div>
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                    <div className="px-3 py-2" style={{ background: 'var(--zone-1)', border: '1px solid var(--line)', borderRadius: 'var(--radius-sm)' }}>
                                        <div className="font-display text-2xl gold">{(result.inserted || 0).toLocaleString()}</div>
                                        <div className="text-xs text-muted">points added</div>
                                    </div>
                                    <div className="px-3 py-2" style={{ background: 'var(--zone-1)', border: '1px solid var(--line)', borderRadius: 'var(--radius-sm)' }}>
                                        <div className="font-display text-2xl" style={{ color: 'var(--muted)' }}>{(result.duplicates_skipped || 0).toLocaleString()}</div>
                                        <div className="text-xs text-muted">duplicates skipped</div>
                                    </div>
                                </div>
                                {(result.quarantined || 0) > 0 &&
                                    <div className="text-xs text-muted">{result.quarantined} rows quarantined (out of range / implausible).</div>}
                                <div className="text-xs text-muted">Parser: {result.parser}</div>
                                <div className="flex justify-end pt-2">
                                    <button type="button" className="btn-gold" onClick={() => { if (onDone) onDone(); onClose(); }}>Done</button>
                                </div>
                            </>
                        )}
                    </div>
                </Dialog>
            );
        }

export { AeaIngestResponseDialog };
